import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { autoHeightDuration, EASE_IN_OUT } from "@/components/ui/mui-transitions";
import { cn } from "@/lib/utils";

// MUI's `Collapse`, as a shared Atlas component.
//
// Ported because it is shared in the donor and because Atlas had no height
// transition at all: the category tree rendered a branch's children as a bare
// conditional, so a branch that the donor slides open over ~230ms appeared at
// its full height on the first frame (#630). A tree that snaps open gives no
// sense of where the new rows came from, which is the reason MUI ships
// `Collapse` with `timeout='auto'` in the first place
// (`CategoriesFieldPaginated.tsx:172-190` wraps each sub-list in one).
//
// The mechanics are MUI's, fact by fact (`Collapse.js`):
//
//   - collapsed is `height: 0; overflow: hidden` (`:53-54`),
//   - entered is `height: auto; overflow: visible` (`:70-71`) — `auto` rather
//     than a pinned pixel height, so a sub-list that grows later still shows,
//   - the transition is `transitions.create('height')` (`:55`), which is the
//     theme's `easeInOut` — `cubic-bezier(0.4, 0, 0.2, 1)`
//     (`createTransitions.js:15`),
//   - `timeout='auto'` derives the duration from the content's height
//     (`:200,243`), which is `getAutoHeightDuration` below,
//   - the category tree passes `unmountOnExit`, so a folded branch's rows are
//     not in the DOM at all — the default here. MUI's own default is the other
//     way, and the spec groups ride it (#695, `GroupRow.tsx:75-80`): a folded
//     group's rows stay mounted under the exited state's `visibility: hidden`
//     (`Collapse.js:84-86`), so the deep link's row still mounts inside one;
//   - `timeout` as a number is a fixed duration — the spec groups' 300ms;
//   - `orientation='horizontal'` (`:58-66`, `:91-115`) moves every fact above
//     from `height` to `width` — the list map's rail (#756). The wrapper and
//     its inner box are then `width: auto; height: 100%`, the wrapper a flex
//     row, so they keep the content's own width: a fold clips the content
//     rather than reflowing it. The enter measures that width with the wrapper
//     briefly out of flow (`position: absolute`, `:172-188`) — in flow, a
//     block inside a zero-width root reads zero. Folded away under
//     `unmountOnExit` it renders **nothing**, root included, as MUI's
//     `Transition` does once exited — a zero-width root would still take its
//     row's `gap`. (The vertical fold keeps its zero-height root, as before:
//     no vertical caller sits in a gapped row.)

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface MuiCollapseProps {
  /** Named `in` because MUI's prop is, and this is a port of MUI's prop. */
  in: boolean;
  /** MUI's numeric `timeout`: a fixed duration in ms. Omitted is `'auto'`. */
  timeoutMs?: number;
  /** MUI's `orientation`: which way it folds. Vertical unless said otherwise. */
  orientation?: "vertical" | "horizontal";
  /** MUI's `unmountOnExit`. True here unless said otherwise — see above. */
  unmountOnExit?: boolean;
  /**
   * `sx={{ overflow: 'auto' }}` — the spec groups' (`GroupRow.tsx:75-80`). An
   * `sx` lands after MUI's state styles, so it holds in every state: open, the
   * body scrolls sideways inside its card rather than spilling out of it.
   */
  overflowAuto?: boolean;
  "data-testid"?: string;
  children: ReactNode;
  className?: string;
}

export function MuiCollapse({
  in: inProp,
  timeoutMs,
  orientation = "vertical",
  unmountOnExit = true,
  overflowAuto = false,
  children,
  className,
  "data-testid": testId,
}: MuiCollapseProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // `unmountOnExit`: the children leave the DOM once the exit has finished,
  // and arrive before the enter can measure them.
  const [mountedState, setMounted] = useState(inProp);
  const mounted = mountedState || !unmountOnExit;
  /** The `in` the effect last acted on; `null` before the first commit. */
  const actedOn = useRef<boolean | null>(null);
  const horizontal = orientation === "horizontal";
  const size = horizontal ? "width" : "height";

  // Adjusted during render, not in an effect. The children have to exist
  // before the layout effect can measure them, and an effect that mounted
  // them would run *after* that measurement — the enter would then be skipped
  // on the pass that was supposed to play it. This is React's documented
  // "adjusting state when a prop changes": the re-render happens before the
  // browser sees anything.
  if (inProp && !mounted) setMounted(true);

  useLayoutEffect(() => {
    const root = rootRef.current;
    // Folded away and rendered as nothing (horizontal, above): the next open
    // is a change of `in`, so it plays the enter.
    if (!root) {
      actedOn.current = inProp;
      return;
    }

    // The first commit is not a transition. MUI's `appear` defaults to false,
    // so a `Collapse` that starts open is simply open — animating here would
    // play every already-expanded branch on mount.
    if (actedOn.current === null) {
      actedOn.current = inProp;
      root.style[size] = inProp ? "auto" : "0px";
      root.style.overflow = overflowAuto ? "auto" : inProp ? "visible" : "hidden";
      if (!inProp && !unmountOnExit) root.style.visibility = "hidden";
      return;
    }
    // Only a change of `in` is a transition. StrictMode runs a mount's layout
    // effect twice, and a first-commit flag alone read the second run as an
    // enter from 0 — every open group on the spec details page played one on
    // arrival, and the short page it made pulled a lines page the source never
    // asks for (#695).
    if (actedOn.current === inProp) return;

    const run = (from: string, to: string, over: number, onDone: () => void) => {
      root.style.overflow = overflowAuto ? "auto" : "hidden";
      root.style[size] = from;
      // Read forces the style change above to land as its own frame; without
      // it the browser coalesces `from` and `to` and nothing animates.
      void root.offsetHeight;
      root.style.transition = over === 0 ? "" : `${size} ${over}ms ${EASE_IN_OUT}`;
      root.style[size] = to;
      if (over === 0) {
        onDone();
        return undefined;
      }
      const done = () => {
        root.style.transition = "";
        onDone();
      };
      root.addEventListener("transitionend", done, { once: true });
      return () => root.removeEventListener("transitionend", done);
    };

    const duration = (height: number) =>
      prefersReducedMotion() ? 0 : (timeoutMs ?? autoHeightDuration(height));

    if (inProp) {
      root.style.visibility = "";
      const wrapper = wrapperRef.current;
      // The children have not mounted yet; the `mounted` flip re-runs this,
      // and the change is acted on then.
      if (!wrapper) return;
      actedOn.current = inProp;
      let target = wrapper.offsetHeight;
      if (horizontal) {
        wrapper.style.position = "absolute";
        target = wrapper.offsetWidth;
        wrapper.style.position = "";
      }
      return run("0px", `${target}px`, duration(target), () => {
        // Back to `auto`, so later growth is not clipped by a stale pixel
        // size (`Collapse.js:70-71`).
        root.style[size] = "auto";
        root.style.overflow = overflowAuto ? "auto" : "visible";
      });
    }

    actedOn.current = inProp;
    const current = horizontal ? root.offsetWidth : root.offsetHeight;
    return run(`${current}px`, "0px", duration(current), () => {
      if (unmountOnExit) setMounted(false);
      else root.style.visibility = "hidden";
    });
  }, [inProp, mounted, timeoutMs, unmountOnExit, overflowAuto, horizontal, size]);

  if (horizontal && !mounted) return null;

  return (
    <div
      ref={rootRef}
      data-slot="mui-collapse"
      data-testid={testId}
      className={cn("overflow-hidden", className)}
    >
      {mounted ? (
        horizontal ? (
          <div ref={wrapperRef} data-slot="mui-collapse-wrapper" className="flex h-full w-auto">
            <div className="h-full w-auto">{children}</div>
          </div>
        ) : (
          <div ref={wrapperRef} data-slot="mui-collapse-wrapper">
            {children}
          </div>
        )
      ) : null}
    </div>
  );
}
